import { ViewModeToggle } from '@components/ViewModeToggle/ViewModeToggle';
import { useLibrariesViewMode } from '@components/ViewModeToggle/useViewModePreference';
import { useWidthTier } from '@lib/hooks/sizing/element-size';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { LibrarySortByHash, useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { SortButton } from '../../../components/SortSelect/SortButton';
import { usePinnedFirstToggle } from '../hooks/usePinnedFirstToggle';
import { LIBRARY_SORT_GROUPS, PINNED_FIRST_LABEL, getLibrarySortLabel } from '../utils/sortOptions';
import css from './LibrariesNav.module.scss';
import { LibrariesOmniFilter } from './LibrariesOmniFilter';
import { LibrariesVerbPills } from './LibrariesVerbPills/LibrariesVerbPills';

interface LibrariesNavProps {
  /** Opens the page-owned create-library modal. */
  onNewLibrary: () => void;
}

/**
 * STAGE 1: below this width `.right` abbreviates — Filters and Sort to glyphs in one divided frosted
 * pill, New library to a filled `+` circle.
 *
 * FIRST, before the verb pills' labels, for the same reason the library header does it in that order
 * (`RIGHT_LABELS_BELOW` there): "Filters", "Fewest media · Pinned first" and "New library" name
 * controls the user already understands, while the verb pills are the surface that TEACHES those two
 * verbs. Losing a teaching label to keep a familiar one is the wrong trade.
 *
 * MEASURED, not estimated — an earlier 1040 was estimated and ~100px too low, which shipped a band
 * where the row overflowed with everything still labelled. Off a 1323px viewport with the nav rail
 * expanded (1055px of row), the fully-labelled row laid out as:
 *
 * - Verb pills **450px** — the agent pill 288→528 and "Find moments" 538→738, each a glyph +
 *   ~16px/500 Inter label + an 18px chevron (they're pickers here) + padding. Measured under the
 *   old "Work with an Agent" label at 16px/500; "Chat with an Agent" is 5px narrower, and the
 *   15px/600 raised pills (`_verb-pill.scss`) shave another ~5px each, so this errs safe.
 * - Right cluster **636px** — view picker 74 (754→828) + Filters 128 (838→966) + sort **268**
 *   (976→1244, at its widest: the longest sort name with "· Pinned first" appended) + New library
 *   132 + the gaps between them.
 * - Plus the row's own 16px gap and 40px of horizontal padding.
 *
 * = **1142px** against 1055 available: an 87px overflow, exactly the clipped "New library" and
 * horizontal scrollbar that was reported. The old derivation put the right cluster at ~546 because it
 * estimated the sort face at ~230; with the qualifier it is 268. The view picker was briefly a single
 * 40px glyph circle (row ~1108) and is two 74px glyphs again (`ViewModeToggle`), so 1142 stands.
 *
 * 1150 rounds that up — abbreviating a little early costs four words, overflowing costs the layout.
 */
const RIGHT_LABELS_BELOW = 1150;

/**
 * STAGE 2: below this width the verb pills become bare frosted circles too.
 *
 * Derived from what remains once stage 1 has run: the 450px of labelled pills + the row's 16px gap +
 * the ABBREVIATED right cluster's ~209 (74 view picker + 77 Filters+Sort group + 40 New library +
 * two 8px gaps) + 40px padding ≈ **715px**. 730 rounds that up, the same erring-high the library
 * header uses for its own pills.
 *
 * Collapsed the pair is 86px, so the narrowest this row ever needs is ~317px — which is what makes
 * `.root`'s `overflow-x` a genuine last resort rather than a regular occurrence.
 *
 * Lower than the library header's equivalent (790) because this row has no title: its left cluster
 * collapses to 86px, where the header's `.headerLeft` holds a 330px floor for the library name. Same
 * derivation, different summand.
 */
const VERBS_COLLAPSE_BELOW = 730;

/**
 * Both stages from ONE measurement of `.root`, via {@link useWidthTier}.
 *
 * Can't oscillate: `.root` is `width: 100%` of a container this row cannot influence, so abbreviating
 * either cluster changes what the row *needs* and never what is measured. Each stage demands strictly
 * less than the one above it (1108 → 681 → 317), so the row only ever gets simpler as it narrows.
 *
 * That is also why both stages measure the ROW rather than each measuring its own cluster: stage 1
 * hands ~427px to the space the verb pills sit in, so a threshold measured over there would
 * re-expand the labels on a NARROWER window. See `useWidthTier`, which records where that shipped.
 */
const ROW_STAGES = [RIGHT_LABELS_BELOW, VERBS_COLLAPSE_BELOW];

export const LibrariesNav = observer(({ onNewLibrary }: LibrariesNavProps) => {
  const libraryManager = useLibraryManager();
  const viewMode = useLibrariesViewMode();
  const pinnedFirstToggle = usePinnedFirstToggle();
  const [rowRef, stage] = useWidthTier<HTMLDivElement>(ROW_STAGES);
  const rightCompact = stage >= 1;
  const verbsCollapsed = stage >= 2;
  // The table's search line is the omnifilter while it shows, so the Filters pill steps aside
  // rather than put a second search box on screen. Phones list libraries without a table.
  const isMobile = useIsMobile();
  const tableShowing = viewMode.value === 'list' && !isMobile;

  return (
    <div className={css.root} ref={rowRef}>
      {/* The verbs lead the row and push the view/filter/sort/create cluster right via their own
          `margin-right: auto` — no wrapper and no `space-between`, so `.right` stays correctly
          right-aligned even when this renders nothing. Desktop-only for free: `.root` is
          `display: none` on mobile, where these verbs live in a library's ⋯ drawer instead. */}
      <LibrariesVerbPills collapsed={verbsCollapsed} />
      <div className={css.right}>
        <ViewModeToggle {...viewMode} />
        {/* Filters and Sort. Abbreviated they share ONE divided frosted pill — the same control the
            mobile overlays show and the library header uses — because both answer "which libraries,
            in what order" where everything else in this row answers something else. Labelled they
            stay two pills; `display: contents` on the wrapper keeps them direct flex children of
            this cluster, so `.right`'s own gap still spaces them.

            One class or the other, never both: they disagree about `display`, and which of two
            equally-specific rules won would otherwise depend on stylesheet order. */}
        <div className={rightCompact ? css.filterSortGroup : css.filterSortPair}>
          {!tableShowing && <LibrariesOmniFilter compact={rightCompact} />}
          {/* Only inside the group — between two standalone pills `.right`'s gap already separates
              them, and a rule there would tie them together. */}
          {rightCompact && !tableShowing && <div className={css.filterSortDivider} />}
          <SortButton
            tooltip="Sort libraries by"
            value={libraryManager.sortByHash}
            selectedLabel={getLibrarySortLabel(libraryManager.sortByHash)}
            // The face names the modifier as well as the sort, because "Most media" over a
            // pinned-first order is the exact mismatch that reads as broken sorting. Still passed
            // when abbreviated: `SortButton` moves both halves into its tooltip, and the pairing is
            // the point either way. At 268px this is the widest thing in the row, which is most of
            // why it overflowed.
            qualifier={libraryManager.pinnedFirst ? PINNED_FIRST_LABEL : undefined}
            options={LIBRARY_SORT_GROUPS}
            onChange={(value) => {
              libraryManager.setSorting(value as LibrarySortByHash);
              trackingStore.librarySortChanged({ sortBy: value });
            }}
            // The escape hatch, and the proof the sort works: turning this off drops the
            // section headers and leaves a strict sort.
            toggle={pinnedFirstToggle}
            compact={rightCompact}
          />
        </div>
        {
          // This upload button used to upload to the Default Library, which is not very useful and not really intuitive.
          // We are removing this until we have a way to let the user choose the library to upload to.
          // Designs on how to do this are in the works.
          /* <UploadButton disabled={!userStore.data?.isVerified} /> */
        }
        {/* Wrap so the unverified overlay covers only the New library button, not the filter. */}
        <div className={classNames(css.newLibraryWrap, rightCompact && css.newLibraryWrapCompact)}>
          <Tooltip title="Create a new collection of media" placement="top" arrow>
            {/* Abbreviated to a bare `+` circle, keeping its FILLED variant rather than going frosted
                like the group beside it: this is the row's lead action, and the verb pills' own
                frosted circles sit at the other end — a frosted create button would read as one more
                secondary verb. Same call as the library header's Add media. */}
            <ActionButton
              className={rightCompact ? css.newLibraryCompact : undefined}
              onClick={onNewLibrary}
              disabled={!userStore.data?.isVerified}
              aria-label={rightCompact ? 'New library' : undefined}
            >
              {rightCompact ? <AddRoundedIcon className={css.newLibraryIcon} /> : 'New library'}
            </ActionButton>
          </Tooltip>
          {!userStore.data?.isVerified && (
            <Tooltip title="Please verify your email to add media and create libraries.">
              <div className={css.verificationOverlay}>&nbsp;</div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
});
